import { useCallback } from "react";
import { useShallow } from "zustand/shallow";

import TableHead from "ds/components/Table/Head";
import { getHeadColumnKey } from "ds/components/Table/Head/helpers";
import { selectVisibleColumns } from "ds/components/Table/hooks/useTableStore/selectors";

import TableSimpleHeadColumn from "./HeadColumn";
import { showTableSimpleSettingsDrawer } from "./SettingsDrawer";
import { TableSimpleStore } from "./types";
import TableSettings from "../Table/Settings";
import { TableSettingsDrawerProps } from "../Table/Settings/DrawerContent";
import { TableHeadInteractiveColumnProps } from "../Table/Head/InteractiveColumn";
import { isStickyLeft, isStickyRight } from "./helpers";

type TableSimpleHeadProps = {
  store: TableSimpleStore;
  ariaLabel: string;
} & Pick<TableSettingsDrawerProps, "trackAnalytics"> &
  Pick<
    TableHeadInteractiveColumnProps,
    "analyticsPage" | "filtersStorageKey" | "sortCallback" | "tableRef" | "sortConfig"
  >;

const TableSimpleHead = ({
  store,
  ariaLabel,
  sortCallback,
  analyticsPage,
  filtersStorageKey,
  trackAnalytics,
  tableRef,
  sortConfig,
}: TableSimpleHeadProps) => {
  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const showTableSettingsDrawer = useCallback(() => {
    showTableSimpleSettingsDrawer({
      store,
      trackAnalytics,
    });
  }, [store, trackAnalytics]);

  return (
    <TableHead>
      {columns.map((column, i) => (
        <TableSimpleHeadColumn
          key={getHeadColumnKey(column)}
          column={column}
          store={store}
          stickyLeft={isStickyLeft(i, isFirstColumnPinned)}
          stickyRight={isStickyRight(i, columns.length)}
          analyticsPage={analyticsPage}
          filtersStorageKey={filtersStorageKey}
          sortCallback={sortCallback}
          tableRef={tableRef}
          sortConfig={sortConfig}
        >
          {i === columns.length - 1 && (
            <TableSettings title={`${ariaLabel} settings`} onPress={showTableSettingsDrawer} />
          )}
        </TableSimpleHeadColumn>
      ))}
    </TableHead>
  );
};

TableSimpleHead.displayName = "DS.TableSimpleHead";

export default TableSimpleHead;
